import { useEffect, useRef, useState, type MouseEvent } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { Popover, PopoverContent, PopoverTrigger } from "@perceptron/ui/components/popover";
import { Slider } from "@perceptron/ui/components/slider";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import { useMediaRemote, useMediaState, type MediaPlayerInstance } from "@vidstack/react";

const MIN_RATE = 0.25;
const MAX_RATE = 3;
const RATE_STEP = 0.05;
const FIXED_PRESETS = [0.25, 1, 1.5, 3];

export function PlaybackRateControl({
  playerRef,
  onOpenChange,
}: {
  playerRef: React.RefObject<MediaPlayerInstance | null>;
  onOpenChange?: (open: boolean) => void;
}) {
  // Native ratechange remains authoritative, including requests made elsewhere.
  const playbackRate = useMediaState("playbackRate", playerRef);
  const remote = useMediaRemote(playerRef);
  const [open, setOpen] = useState(false);
  const [customRate, setCustomRate] = useState(1.75);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const sliderRef = useRef<HTMLSpanElement>(null);
  const pointerInteraction = useRef(false);
  const rateLabel = `${playbackRate}×`;
  const presets = [0.25, 1, 1.5, customRate, 3];

  useEffect(() => {
    // Keep desktop/mobile shortcuts in sync with the accepted native rate.
    if (playbackRate >= MIN_RATE && playbackRate <= MAX_RATE && !FIXED_PRESETS.includes(playbackRate)) {
      setCustomRate(playbackRate);
    }
  }, [playbackRate]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  const changeRate = (rate: number) => {
    // Keep repeated +/- steps exact instead of displaying floating-point tails.
    const nextRate = Math.round(Math.min(MAX_RATE, Math.max(MIN_RATE, rate)) * 100) / 100;
    remote.changePlaybackRate(nextRate);
  };
  const chooseRate = (event: MouseEvent<HTMLButtonElement>, rate: number) => {
    changeRate(rate);
    if (event.detail > 0) event.currentTarget.blur();
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={formatString(strings.videoPlayer.playbackSpeedCurrent, rateLabel)}
          className="min-w-10 rounded-sm px-2 py-1 tabular-nums text-foreground-secondary transition-colors legal hover:bg-background-alt-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring data-[state=open]:bg-background-alt-2 data-[state=open]:text-foreground"
          ref={triggerRef}
          title={strings.videoPlayer.playbackSpeed}
          type="button"
          onKeyDown={() => {
            pointerInteraction.current = false;
          }}
          onPointerDown={() => {
            pointerInteraction.current = true;
          }}
        >
          {rateLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label={strings.videoPlayer.playbackSpeed}
        className="dark w-72 max-w-[calc(100vw-24px)] rounded-2xl border-white/10 bg-[color-mix(in_srgb,var(--background-alt-2)_88%,transparent)] p-2.5 text-foreground shadow-none backdrop-blur-xl"
        collisionPadding={12}
        portalContainer={playerRef.current?.el}
        side="top"
        sideOffset={8}
        onCloseAutoFocus={(event) => {
          if (pointerInteraction.current) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          // Escape dismisses this panel before the player's focus lock.
          event.preventDefault();
          pointerInteraction.current = false;
          changeOpen(false);
        }}
        onKeyDown={(event) => {
          pointerInteraction.current = false;
          // Slider/buttons own their keys; Home/End must not seek the video.
          event.stopPropagation();
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (pointerInteraction.current) triggerRef.current?.blur();
          else sliderRef.current?.querySelector<HTMLElement>('[role="slider"]')?.focus();
        }}
        onPointerDown={() => {
          pointerInteraction.current = true;
        }}
        onPointerDownOutside={() => {
          pointerInteraction.current = true;
        }}
      >
        <div aria-hidden="true" className="flex items-baseline justify-center gap-0.5 pb-1 pt-0.5 tabular-nums">
          <span className="title-3 [--title-3-font-size:1.375rem]">{playbackRate}</span>
          <span className="text-foreground-secondary legal [--legal-font-size:0.625rem]">×</span>
        </div>
        <div className="flex items-center gap-px">
          <Button
            aria-label={strings.videoPlayer.decreasePlaybackSpeed}
            className="size-7 shrink-0 rounded-[10px] border border-white/10 bg-background-alt shadow-none"
            disabled={playbackRate <= MIN_RATE}
            size="icon"
            type="button"
            variant="ghost"
            onClick={(event) => chooseRate(event, playbackRate - RATE_STEP)}
          >
            <Icon aria-hidden="true" className="size-3.5" name="minus" />
          </Button>
          <Slider
            aria-label={strings.videoPlayer.playbackSpeed}
            aria-valuetext={rateLabel}
            className="h-7 min-w-0 cursor-pointer [&_[data-slot=slider-range]]:bg-transparent [&_[data-slot=slider-thumb]]:size-3 [&_[data-slot=slider-thumb]]:border-primary [&_[data-slot=slider-thumb]]:bg-[linear-gradient(var(--primary),color-mix(in_srgb,var(--primary)_65%,var(--background-alt-1)))] [&_[data-slot=slider-thumb]]:shadow-none [&_[data-slot=slider-track]]:h-px [&_[data-slot=slider-track]]:bg-[linear-gradient(to_right,transparent_0%,var(--foreground-secondary)_14%,var(--foreground-secondary)_86%,transparent_100%)]"
            max={MAX_RATE}
            min={MIN_RATE}
            ref={sliderRef}
            step={RATE_STEP}
            value={[playbackRate]}
            onPointerUp={(event) => {
              const focused = event.currentTarget.ownerDocument.activeElement;
              if (focused instanceof HTMLElement && event.currentTarget.contains(focused)) focused.blur();
            }}
            onValueChange={([rate]) => {
              if (rate !== undefined) changeRate(rate);
            }}
          />
          <Button
            aria-label={strings.videoPlayer.increasePlaybackSpeed}
            className="size-7 shrink-0 rounded-[10px] border border-white/10 bg-background-alt shadow-none"
            disabled={playbackRate >= MAX_RATE}
            size="icon"
            type="button"
            variant="ghost"
            onClick={(event) => chooseRate(event, playbackRate + RATE_STEP)}
          >
            <Icon aria-hidden="true" className="size-3.5" name="plus" />
          </Button>
        </div>
        <div
          aria-label={strings.videoPlayer.playbackSpeedPresets}
          className="mt-2 flex items-start gap-1.5"
          role="group"
        >
          {presets.map((rate) => (
            <div className="min-w-0 flex-1 text-center" key={rate}>
              <Button
                aria-label={formatString(strings.videoPlayer.setPlaybackSpeed, `${rate}×`)}
                aria-pressed={playbackRate === rate}
                className={cn(
                  "h-[22px] w-full rounded-[10px] border border-white/5 bg-background-alt px-0 tabular-nums shadow-none legal [--legal-font-size:0.625rem] [--legal-font-weight:500] [--legal-line-height:0.75rem]",
                  playbackRate === rate &&
                    "bg-primary bg-gradient-to-b from-primary to-[color-mix(in_srgb,var(--primary)_88%,var(--background-alt-1))] text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                )}
                size="sm"
                type="button"
                variant="ghost"
                onClick={(event) => chooseRate(event, rate)}
              >
                {rate}
              </Button>
              {(rate === 1 || rate === customRate) && (
                <span className="mt-0.5 block text-foreground-secondary legal [--legal-font-size:0.5rem] [--legal-line-height:0.75rem]">
                  {rate === 1 ? strings.videoPlayer.normalSpeed : strings.videoPlayer.customSpeed}
                </span>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
